import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOPIC_FILTERS = [
  "All",
  "Newcomer Essentials",
  "Budgeting",
  "Credit Building",
  "Mortgages",
  "Insurance",
  "Cost of Living",
];

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => { setArticles(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = articles.filter((a) => {
    const matchTopic = filter === "All" || a.topic === filter;
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchTopic && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-3">Articles</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] tracking-tight">
            Honest articles. Built around real Canadian numbers.
          </h1>
          <p className="text-[#57534E] mt-4 text-lg max-w-2xl leading-relaxed">
            Every piece on this site has to pass one test: does it actually help you make a better financial decision in Canada?
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 px-6 border-b border-[#E7E5E4] bg-white sticky top-16 z-10">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Topic filters */}
          <div className="flex flex-wrap gap-2" data-testid="topic-filters">
            {TOPIC_FILTERS.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                data-testid={`filter-${t.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  filter === t
                    ? "bg-[#1B4332] text-white border-[#1B4332]"
                    : "border-[#E7E5E4] text-[#57534E] hover:border-[#1B4332] hover:text-[#1B4332]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              data-testid="article-search"
              className="w-full pl-8 pr-4 py-2 border border-[#E7E5E4] rounded-md text-sm focus:outline-none focus:border-[#1B4332] bg-[#F9F8F6] text-[#1C1C1C] placeholder-[#A8A29E]"
            />
          </div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-12 px-6">
        <div className="max-w-screen-xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] rounded-lg p-6 animate-pulse">
                  <div className="h-3 bg-[#F2EFE9] rounded w-1/3 mb-4" />
                  <div className="h-5 bg-[#F2EFE9] rounded w-full mb-2" />
                  <div className="h-5 bg-[#F2EFE9] rounded w-3/4 mb-4" />
                  <div className="h-3 bg-[#F2EFE9] rounded w-full mb-2" />
                  <div className="h-3 bg-[#F2EFE9] rounded w-5/6" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#57534E]">
              <p className="text-lg font-medium">No articles found.</p>
              <button onClick={() => { setFilter("All"); setSearch(""); }} className="mt-4 text-[#1B4332] font-semibold text-sm hover:underline">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/articles/${a.slug}`}
                  data-testid={`article-item-${i}`}
                  className="bg-white border border-[#E7E5E4] rounded-lg p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group flex flex-col"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="topic-badge">{a.topic}</p>
                    <span className="text-[#A8A29E] text-xs">{a.read_time}</span>
                  </div>
                  <h3 className="font-bold text-[#1C1C1C] text-base leading-snug group-hover:text-[#1B4332] transition-colors mb-2 flex-1">
                    {a.title}
                  </h3>
                  <p className="text-[#57534E] text-sm leading-relaxed line-clamp-3 mb-4">{a.excerpt}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <p className="text-[#A8A29E] text-xs">
                      {new Date(a.created_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                    <ArrowRight size={14} className="text-[#1B4332] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
